import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TablePagination,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add,
  DeleteSweep,
  Delete,
  Edit,
  Refresh,
  Search
} from '@mui/icons-material';
import { useAuth } from '@clerk/clerk-react';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import JoiItemForm from './JoiItemForm';

const TABS = [
  { id: 'tasks', label: 'Tasks' },
  { id: 'comments', label: 'Comments' },
  { id: 'disobeys', label: 'Disobeys' },
  { id: 'item-pairs', label: 'Item Pairs' }
];

const TAB_TO_PATH = {
  tasks: '/api/joi/tasks',
  comments: '/api/joi/comments',
  disobeys: '/api/joi/disobeys',
  'item-pairs': '/api/joi/item-pairs'
};

const TASK_TYPE_COLOR = {
  Reward: 'success',
  Punishment: 'error',
  Neutral: 'default'
};

const RATING_COLOR = {
  Light: 'info',
  Normal: 'default',
  Extreme: 'warning',
  Hardcore: 'error'
};

const JoiPanel = ({ kinks = [], realItems = [], onNotify }) => {
  const { getToken } = useAuth();
  const [activeTab, setActiveTab] = useState('tasks');
  const [data, setData] = useState({
    tasks: [],
    comments: [],
    disobeys: [],
    'item-pairs': []
  });
  const [loading, setLoading] = useState({
    tasks: false,
    comments: false,
    disobeys: false,
    'item-pairs': false
  });
  const [error, setError] = useState({
    tasks: '',
    comments: '',
    disobeys: '',
    'item-pairs': ''
  });
  const [page, setPage] = useState({ tasks: 0, comments: 0, disobeys: 0, 'item-pairs': 0 });
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [confirmClearAllOpen, setConfirmClearAllOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const kinkById = useMemo(() => {
    const m = new Map();
    for (const k of kinks) m.set(String(k._id), k.name);
    return m;
  }, [kinks]);

  const realItemById = useMemo(() => {
    const m = new Map();
    for (const ri of realItems) m.set(String(ri._id), ri.name);
    return m;
  }, [realItems]);

  const fetchTab = useCallback(async (tabId) => {
    setLoading((prev) => ({ ...prev, [tabId]: true }));
    setError((prev) => ({ ...prev, [tabId]: '' }));
    try {
      const res = await fetch(`${API_BASE_URL}${TAB_TO_PATH[tabId]}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData((prev) => ({ ...prev, [tabId]: Array.isArray(json) ? json : [] }));
    } catch (err) {
      setError((prev) => ({ ...prev, [tabId]: err.message || 'Failed to load' }));
      setData((prev) => ({ ...prev, [tabId]: [] }));
    } finally {
      setLoading((prev) => ({ ...prev, [tabId]: false }));
    }
  }, []);

  useEffect(() => {
    fetchTab('tasks');
    fetchTab('comments');
    fetchTab('disobeys');
    fetchTab('item-pairs');
  }, [fetchTab]);

  const handleTabChange = (_e, v) => {
    setActiveTab(v);
    setSearch('');
  };

  const resolveFetishName = useCallback(
    (id) => kinkById.get(String(id)) || String(id),
    [kinkById]
  );
  const resolveRealItemName = useCallback(
    (id) => realItemById.get(String(id)) || String(id),
    [realItemById]
  );

  const filteredRows = useMemo(() => {
    const rows = data[activeTab] || [];
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((row) => JSON.stringify(row).toLowerCase().includes(q));
  }, [data, activeTab, search]);

  const pageValue = page[activeTab] || 0;
  const pageRows = useMemo(
    () => filteredRows.slice(pageValue * rowsPerPage, pageValue * rowsPerPage + rowsPerPage),
    [filteredRows, pageValue, rowsPerPage]
  );

  const handleChangePage = (_e, newPage) => {
    setPage((prev) => ({ ...prev, [activeTab]: newPage }));
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage((prev) => ({ ...prev, [activeTab]: 0 }));
  };

  const handleOpenCreate = () => {
    setEditingItem(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditingItem(row);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    if (submitting) return;
    setFormOpen(false);
    setEditingItem(null);
  };

  const handleSubmitForm = async (payload) => {
    setSubmitting(true);
    try {
      const isEdit = Boolean(editingItem && editingItem._id);
      const baseUrl = `${API_BASE_URL}${TAB_TO_PATH[activeTab]}`;
      const url = isEdit ? `${baseUrl}/${editingItem._id}` : baseUrl;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders(getToken))
        },
        body: JSON.stringify(payload)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
      await fetchTab(activeTab);
      onNotify?.(isEdit ? 'Saved' : 'Created', 'success');
      setFormOpen(false);
      setEditingItem(null);
    } catch (err) {
      onNotify?.(err.message || 'Failed to save', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestDelete = (row) => {
    setDeleteTarget(row);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}${TAB_TO_PATH[activeTab]}/${deleteTarget._id}`,
        {
          method: 'DELETE',
          headers: await getAuthHeaders(getToken)
        }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
      await fetchTab(activeTab);
      onNotify?.('Deleted', 'success');
      setDeleteTarget(null);
    } catch (err) {
      onNotify?.(err.message || 'Failed to delete', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleClearAll = async () => {
    setClearing(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/joi/clear-all`, {
        method: 'DELETE',
        headers: await getAuthHeaders(getToken)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
      onNotify?.(
        `Cleared: tasks ${json.deleted?.tasks ?? 0}, comments ${json.deleted?.comments ?? 0}, disobeys ${json.deleted?.disobeys ?? 0}, pairs ${json.deleted?.itemPairs ?? 0}`,
        'success'
      );
      setConfirmClearAllOpen(false);
      setPage({ tasks: 0, comments: 0, disobeys: 0, 'item-pairs': 0 });
      await Promise.all([
        fetchTab('tasks'),
        fetchTab('comments'),
        fetchTab('disobeys'),
        fetchTab('item-pairs')
      ]);
    } catch (err) {
      onNotify?.(err.message || 'Failed to clear JOI data', 'error');
    } finally {
      setClearing(false);
    }
  };

  const counts = {
    tasks: data.tasks.length,
    comments: data.comments.length,
    disobeys: data.disobeys.length,
    'item-pairs': data['item-pairs'].length
  };

  const singularLabel = {
    tasks: 'task',
    comments: 'comment',
    disobeys: 'disobey',
    'item-pairs': 'item pair'
  }[activeTab];

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          JOI
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
            New {singularLabel}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              fetchTab('tasks');
              fetchTab('comments');
              fetchTab('disobeys');
              fetchTab('item-pairs');
            }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteSweep />}
            onClick={() => setConfirmClearAllOpen(true)}
          >
            Clear all JOI data
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          {TABS.map((t) => (
            <Tab
              key={t.id}
              value={t.id}
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{t.label}</span>
                  <Chip size="small" label={counts[t.id]} />
                </Stack>
              }
            />
          ))}
        </Tabs>
      </Paper>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search in current tab..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage((prev) => ({ ...prev, [activeTab]: 0 }));
          }}
          InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ flex: 1, maxWidth: 480 }}
        />
      </Stack>

      {error[activeTab] && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error[activeTab]}
        </Alert>
      )}

      {loading[activeTab] ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <Stack spacing={1.5}>
            {pageRows.length === 0 && (
              <Paper sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                Nothing to show
              </Paper>
            )}
            {activeTab === 'tasks' &&
              pageRows.map((row) => (
                <TaskCard
                  key={row._id}
                  row={row}
                  resolveFetishName={resolveFetishName}
                  resolveRealItemName={resolveRealItemName}
                  onEdit={() => handleOpenEdit(row)}
                  onDelete={() => handleRequestDelete(row)}
                />
              ))}
            {activeTab === 'comments' &&
              pageRows.map((row) => (
                <CommentCard
                  key={row._id}
                  row={row}
                  resolveFetishName={resolveFetishName}
                  resolveRealItemName={resolveRealItemName}
                  onEdit={() => handleOpenEdit(row)}
                  onDelete={() => handleRequestDelete(row)}
                />
              ))}
            {activeTab === 'disobeys' &&
              pageRows.map((row) => (
                <DisobeyCard
                  key={row._id}
                  row={row}
                  onEdit={() => handleOpenEdit(row)}
                  onDelete={() => handleRequestDelete(row)}
                />
              ))}
            {activeTab === 'item-pairs' &&
              pageRows.map((row) => (
                <ItemPairCard
                  key={row._id}
                  row={row}
                  resolveRealItemName={resolveRealItemName}
                  onEdit={() => handleOpenEdit(row)}
                  onDelete={() => handleRequestDelete(row)}
                />
              ))}
          </Stack>

          <Paper sx={{ mt: 2 }}>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={pageValue}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100, 200]}
            />
          </Paper>
        </Box>
      )}

      <JoiItemForm
        open={formOpen}
        onClose={handleCloseForm}
        onSubmit={handleSubmitForm}
        tab={activeTab}
        item={editingItem}
        kinks={kinks}
        realItems={realItems}
      />

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Delete {singularLabel}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently remove this record. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmClearAllOpen} onClose={() => !clearing && setConfirmClearAllOpen(false)}>
        <DialogTitle>Clear all JOI data?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete all JOI tasks ({counts.tasks}), comments ({counts.comments}),
            disobeys ({counts.disobeys}) and item pairs ({counts['item-pairs']}). Kinks and Real Items
            catalogs are not affected. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClearAllOpen(false)} disabled={clearing}>
            Cancel
          </Button>
          <Button onClick={handleClearAll} color="error" variant="contained" disabled={clearing}>
            {clearing ? 'Clearing...' : 'Clear everything'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const cardSx = {
  p: 2,
  border: 1,
  borderColor: 'divider'
};

const CardActions = ({ onEdit, onDelete }) => (
  <Stack direction="row" spacing={0.5}>
    <Tooltip title="Edit">
      <IconButton size="small" onClick={onEdit}>
        <Edit fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title="Delete">
      <IconButton size="small" color="error" onClick={onDelete}>
        <Delete fontSize="small" />
      </IconButton>
    </Tooltip>
  </Stack>
);

const TextBlockColumn = ({ title, blocks, fallback }) => {
  const hasBlocks =
    blocks && (blocks.preparation || blocks.task || blocks.completion);
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
        {title}
      </Typography>
      {hasBlocks ? (
        <Stack spacing={0.75}>
          {blocks.preparation && (
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Preparation
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {blocks.preparation}
              </Typography>
            </Box>
          )}
          {blocks.task && (
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Task
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {blocks.task}
              </Typography>
            </Box>
          )}
          {blocks.completion && (
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Completion
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {blocks.completion}
              </Typography>
            </Box>
          )}
        </Stack>
      ) : (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {fallback || (
            <Box component="span" sx={{ color: 'text.secondary' }}>
              —
            </Box>
          )}
        </Typography>
      )}
    </Box>
  );
};

const TaskCard = ({ row, resolveFetishName, resolveRealItemName, onEdit, onDelete }) => {
  return (
    <Paper sx={cardSx} elevation={0}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={row.taskType || 'Neutral'}
            color={TASK_TYPE_COLOR[row.taskType] || 'default'}
          />
          <Chip
            size="small"
            label={row.rating || 'Normal'}
            color={RATING_COLOR[row.rating] || 'default'}
            variant="outlined"
          />
          {row.taskForm && <Chip size="small" label={`form: ${row.taskForm}`} variant="outlined" />}
          {Number(row.count) > 0 && <Chip size="small" label={`×${row.count}`} />}
          {Number(row.duration) > 0 && <Chip size="small" label={`${row.duration}s`} />}
          <Chip size="small" label={`score ${row.score ?? 0}`} variant="outlined" />
          {row.foreplay && <Chip size="small" label="foreplay" color="info" variant="outlined" />}
          {row.improved && <Chip size="small" label="improved" color="secondary" variant="outlined" />}
        </Stack>
        <CardActions onEdit={onEdit} onDelete={onDelete} />
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 1.5 }}>
        <TextBlockColumn title="English" blocks={row.englishTaskBlocks} fallback={row.english} />
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
        <TextBlockColumn title="Russian" blocks={row.russianTaskBlocks} fallback={row.russian} />
      </Stack>

      <Divider sx={{ my: 1 }} />

      <Stack spacing={0.75}>
        <ChipListRow
          title="Fetishes"
          values={(row.requiredFetishes || []).map(resolveFetishName)}
        />
        <ChipListRow
          title="Required items"
          values={(row.requiredItems || []).map(
            (it) => `${resolveRealItemName(it.item)} · ${it.state || 'Available'}`
          )}
        />
        {Array.isArray(row.newStateItems) && row.newStateItems.length > 0 && (
          <ChipListRow
            title="New state"
            values={row.newStateItems.map(
              (it) => `${resolveRealItemName(it.item)} → ${it.state || 'Available'}`
            )}
          />
        )}
      </Stack>
    </Paper>
  );
};

const CommentCard = ({ row, resolveFetishName, resolveRealItemName, onEdit, onDelete }) => (
  <Paper sx={cardSx} elevation={0}>
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      spacing={1}
      sx={{ mb: 1 }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip size="small" label={row.type || 'Contract'} />
        <Chip
          size="small"
          label={`form: ${row.preferredFormAddress || 'Any'}`}
          variant="outlined"
        />
      </Stack>
      <CardActions onEdit={onEdit} onDelete={onDelete} />
    </Stack>

    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 1.5 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
          English
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {row.english || '—'}
        </Typography>
      </Box>
      <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
          Russian
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {row.russian || '—'}
        </Typography>
      </Box>
    </Stack>

    <Divider sx={{ my: 1 }} />

    <Stack spacing={0.75}>
      <ChipListRow
        title="Fetishes"
        values={(row.requiredFetishes || []).map(resolveFetishName)}
      />
      <ChipListRow
        title="Items"
        values={(row.requiredItems || []).map(resolveRealItemName)}
      />
    </Stack>
  </Paper>
);

const DisobeyCard = ({ row, onEdit, onDelete }) => (
  <Paper sx={cardSx} elevation={0}>
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      spacing={1}
      sx={{ mb: 1 }}
    >
      <Chip size="small" label={`repeat #${row.repeatNumber ?? 0}`} />
      <CardActions onEdit={onEdit} onDelete={onDelete} />
    </Stack>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
          English
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {row.english || '—'}
        </Typography>
      </Box>
      <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
          Russian
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {row.russian || '—'}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const ItemPairCard = ({ row, resolveRealItemName, onEdit, onDelete }) => (
  <Paper sx={cardSx} elevation={0}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip size="small" label={resolveRealItemName(row.item1)} />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          ↔
        </Typography>
        <Chip size="small" label={resolveRealItemName(row.item2)} />
      </Stack>
      <CardActions onEdit={onEdit} onDelete={onDelete} />
    </Stack>
  </Paper>
);

const ChipListRow = ({ title, values }) => (
  <Stack direction="row" spacing={1} alignItems="flex-start">
    <Typography
      variant="caption"
      sx={{ color: 'text.secondary', minWidth: 100, pt: 0.5 }}
    >
      {title}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {values && values.length > 0 ? (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {values.map((v, idx) => (
            <Chip key={`${v}-${idx}`} label={v} size="small" variant="outlined" />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          —
        </Typography>
      )}
    </Box>
  </Stack>
);

export default JoiPanel;
